import { useState, useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const THROTTLE_MS = 1000;

/**
 * Hook that detects user inactivity and triggers a warning/timeout.
 * @param {Object} options
 * @param {number} options.timeoutMs - Total inactivity timeout in ms (default: 10 min)
 * @param {number} options.warningMs - Warning period before timeout in ms (default: 60s)
 * @param {function} options.onTimeout - Callback when timeout expires
 * @param {boolean} options.enabled - Whether the hook is active
 * @returns {{ showWarning: boolean, secondsLeft: number, resetTimer: function }}
 */
export function useInactivityTimeout({
  timeoutMs = 10 * 60 * 1000,
  warningMs = 60 * 1000,
  onTimeout,
  enabled = true
} = {}) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000));

  const warningTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef = useRef(onTimeout);

  // Keep callback ref current
  onTimeoutRef.current = onTimeout;

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    timeoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const startTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setSecondsLeft(Math.floor(warningMs / 1000));
    lastActivityRef.current = Date.now();

    const warningDelay = timeoutMs - warningMs;

    // Timer to show warning
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(Math.floor(warningMs / 1000));

      // Start countdown
      countdownRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Timer to trigger timeout
      timeoutTimerRef.current = setTimeout(() => {
        setShowWarning(false);
        onTimeoutRef.current?.();
      }, warningMs);
    }, warningDelay);
  }, [timeoutMs, warningMs, clearAllTimers]);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    startTimers();
  }, [enabled, startTimers]);

  // Handle activity events with throttling
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    startTimers();

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;

      // Only reset if warning is not yet showing
      if (!warningTimerRef.current) return;
      startTimers();
    };

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, startTimers, clearAllTimers]);

  // Handle visibility change - reset timer when tab becomes visible
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we should have timed out while hidden
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= timeoutMs) {
          setShowWarning(false);
          onTimeoutRef.current?.();
        } else if (elapsed >= timeoutMs - warningMs) {
          // Should be in warning state
          const remaining = timeoutMs - elapsed;
          setShowWarning(true);
          setSecondsLeft(Math.ceil(remaining / 1000));

          clearAllTimers();
          countdownRef.current = setInterval(() => {
            setSecondsLeft(prev => {
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          timeoutTimerRef.current = setTimeout(() => {
            setShowWarning(false);
            onTimeoutRef.current?.();
          }, remaining);
        } else {
          startTimers();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, timeoutMs, warningMs, startTimers, clearAllTimers]);

  return { showWarning, secondsLeft, resetTimer };
}
